import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Pencil } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Sheet } from '@/components/ui/Sheet';
import { Kicker, Lead, Meta, PageTitle, Row, RowList } from '@/components/ui/Surface';
import { useBackendSurveyStore } from '@/store/backendSurveyStore';
import { useDoctorProfilesStore } from '@/store/doctorProfilesStore';
import { useUiStore } from '@/store/uiStore';
import { QuestionField, draftOf, hasSavedAnswer, isAnswered, isRequired } from '@/features/registration/QuestionField';
import type { QuestionDraft } from '@/features/registration/QuestionField';
import { isEmptyAnswerChosen } from '@/features/registration/questionRules';
import { questionParts } from '@/lib/titles';
import type { AnswerEntry, QuestionSchema } from '@/api/miniapp/types';

/**
 * Анкета активного профиля после регистрации.
 *
 * Анкету заполняют один раз при входе, но ответы устаревают: специализация
 * меняется, услуги добавляются. Экран показывает заполненное и даёт изменить
 * любой ответ, не проходя анкету заново.
 *
 * Состояния: загрузка, ошибка с повтором, пусто, список ответов, правка.
 */
export function ProfileSurveyScreen() {
  const navigate = useNavigate();
  const toast = useUiStore((state) => state.toast);

  const questions = useBackendSurveyStore((state) => state.questions);
  const answers = useBackendSurveyStore((state) => state.answers);
  const loading = useBackendSurveyStore((state) => state.loading);
  const saving = useBackendSurveyStore((state) => state.saving);
  const error = useBackendSurveyStore((state) => state.error);
  const reload = useBackendSurveyStore((state) => state.reload);
  const saveAnswer = useBackendSurveyStore((state) => state.saveAnswer);

  const profiles = useDoctorProfilesStore((state) => state.profiles);
  const activeId = useDoctorProfilesStore((state) => state.activeId);
  const activeProfile = profiles.find((profile) => profile.doctor_profile_id === activeId);

  const [editing, setEditing] = useState<QuestionSchema | null>(null);
  const [draft, setDraft] = useState<QuestionDraft>({ value: null, otherValue: '', comment: '' });

  useEffect(() => {
    void reload();
  }, [reload]);

  const startEdit = (question: QuestionSchema) => {
    setDraft(draftOf(answers[question.key]));
    setEditing(question);
  };

  const submitEdit = async () => {
    if (!editing) return;

    const saved = await saveAnswer({
      key: editing.key,
      value: draft.value,
      otherValue: draft.otherValue || undefined,
      comment: draft.comment || undefined,
    });

    // При неудаче окно остаётся открытым: закрыть его — значит потерять ответ
    if (!saved) {
      toast(useBackendSurveyStore.getState().error ?? 'Ответ не сохранён', 'error');
      return;
    }

    setEditing(null);
    toast('Ответ сохранён', 'success');
  };

  if (loading && questions.length === 0) {
    return (
      <Screen subtitle="Анкета профиля" back backTo="/profile" tab="profile">
        <PanelSkeleton count={4} />
      </Screen>
    );
  }

  if (error && questions.length === 0) {
    return (
      <Screen subtitle="Анкета профиля" back backTo="/profile" tab="profile">
        <LoadFailure title="Анкета не загрузилась" description={error} onRetry={() => void reload()} />
      </Screen>
    );
  }

  if (questions.length === 0) {
    return (
      <Screen subtitle="Анкета профиля" back backTo="/profile" tab="profile">
        <StateView
          icon={<ClipboardList className="h-6 w-6" aria-hidden />}
          title="Вопросов пока нет"
          description="Анкета для этого профиля ещё не настроена."
          primary={
            <Button variant="primary" block onClick={() => navigate('/profile')}>
              Вернуться в профиль
            </Button>
          }
        />
      </Screen>
    );
  }

  const required = questions.filter(isRequired);
  // Счётчик — о наличии ответов, а не о строгости правил поля: ответ,
  // сохранённый до изменения схемы, всё равно ответ
  const filled = required.filter((question) => hasSavedAnswer(question, answers[question.key])).length;

  return (
    <Screen subtitle="Анкета профиля" back backTo="/profile" tab="profile">
      <Kicker>{activeProfile?.profile_name ?? 'Активный профиль'}</Kicker>
      <PageTitle>Анкета</PageTitle>
      <Lead>
        По этим ответам ассистент понимает вашу практику. Нажмите на вопрос, чтобы изменить ответ.
      </Lead>
      <Meta>
        Заполнено {filled} из {required.length}
      </Meta>

      <RowList className="mt-3">
        {questions.map((question) => (
          <div key={question.key}>
            <Row
              align="start"
              title={questionParts(question).title}
              subtitle={summarize(question, answers[question.key])}
              trailing={<Pencil className="h-4 w-4 text-muted" aria-hidden />}
              onClick={() => startEdit(question)}
            />
          </div>
        ))}
      </RowList>

      <Sheet open={editing !== null} onClose={() => setEditing(null)} title="Изменить ответ">
        {editing ? (
          <>
            <p className="mb-3 text-[13px] font-medium">{questionParts(editing).title}</p>
            {editing.description ? (
              <p className="mb-3 text-[12px] leading-[1.45] text-muted">{editing.description}</p>
            ) : null}

            <QuestionField question={editing} draft={draft} onChange={setDraft} />

            <div className="mt-4 grid gap-2">
              <Button
                variant="primary"
                block
                loading={saving}
                disabled={!isAnswered(editing, draft)}
                onClick={() => void submitEdit()}
              >
                Сохранить
              </Button>
              <Button variant="ghost" block onClick={() => setEditing(null)}>
                Отменить
              </Button>
            </div>
          </>
        ) : null}
      </Sheet>
    </Screen>
  );
}

/** Короткая выжимка ответа для списка. */
function summarize(question: QuestionSchema, entry: AnswerEntry | undefined): string {
  const value = entry?.value;
  if (value == null) return 'Не заполнено';

  if (Array.isArray(value)) {
    const items = value.filter(Boolean);
    if (items.length > 0) return items.join(', ');
    // Ответ «нечего указать» — тоже ответ, и показывается его формулировкой
    return isEmptyAnswerChosen(question, value) && question.emptyAnswerText
      ? question.emptyAnswerText
      : 'Не заполнено';
  }

  if (typeof value === 'object') {
    // Ответ группы: показываются только заполненные поля
    const parts = Object.values(value).filter((item): item is string => Boolean(item));
    return parts.length > 0 ? parts.join(', ') : 'Не заполнено';
  }

  return String(value).trim() || 'Не заполнено';
}
